import { Color } from 'types/basic.types';
import { WidgetId } from 'types/idTypes';
import { CellValue } from 'types/spreadsheet';
import { AccessibleElement, Widget, WidgetType } from 'types/widget.types';

export const HighChartsWidgetTypesValue = [
  WidgetType.AreaChart,
  WidgetType.BarChart,
  WidgetType.ColumnChart,
  WidgetType.HalfPieChart,
  WidgetType.HistogramChart,
  WidgetType.LineChart,
  WidgetType.PieChart,
  WidgetType.StackedAreaChart,
  WidgetType.StackedBarChart,
  WidgetType.StackedColumnChart,
  WidgetType.WordCloudChart,
  WidgetType.ComboChart,
] as const;

export type HighChartsWidgetTypes = typeof HighChartsWidgetTypesValue[number];

/**
 * A type guard function to check if the widgetType is a valid highcharts widget type
 * @param {unknown} widgetType any widget type
 * @returns true if the widgetType is a valid highcharts widget type
 */
export const isHighChartsWidgetType = (widgetType: unknown): widgetType is HighChartsWidgetTypes => {
  return HighChartsWidgetTypesValue.includes(widgetType as HighChartsWidgetTypes);
};

export const ALLOWED_PATTERN_IDS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const;
export const ENABLED_PATTERN_IDS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 14] as const;

export type PatternId = typeof ALLOWED_PATTERN_IDS[number];

export type FontStyleOptions = {
  color: Color;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  fontStyle: string;
  textDecoration: string;
};

export type ChartLegendData = {
  enabled: boolean;
  labelStyle: FontStyleOptions;
};

type ChartLabelDataType = {
  // Do we allow to display the data label on the charts.
  // Ex: the data value on top of a bar
  enabled: boolean;

  // Adornment for the data label
  prefix?: string;
  suffix?: string;

  // Adornment for the data label, which belongs to the second y-axis
  y2Prefix?: string;
  y2Suffix?: string;

  // The style of the data label
  style: FontStyleOptions;

  // if the category should be visible in the label
  categoryEnabled?: boolean;
  // if the value should be visible in the label
  valueEnabled?: boolean;
};

export interface xAxis extends Axis {
  categories?: string[];
}

export interface yAxis extends Axis {}

export interface Axis {
  title: {
    text: string;
    style: FontStyleOptions;
  };
  enableLine?: boolean;
  enablelabels?: boolean;
  style: {
    lineColor: string;
    lineWidth: number;
  };
  labelStyle: FontStyleOptions;
}

export interface Grid {
  style: {
    lineColor: string;
  };
}

export interface SpreadsheetRow {
  [name: string]: CellValue;
  color?: Color;
}

export interface UseHighchartsDataProps {
  seriesData: ChartWidgetData['seriesData'];
  seriesMeta: ChartWidgetData['seriesMeta'];

  // Are we allow to generate a single series data when the data is not enough for multi series
  allowSingleSeries?: boolean;

  // Are we allow to generate a single series data even if there are more data than request by single series
  forceSingleSeries?: boolean;

  // Do we use the header to determine if it is a empty column
  disableColumnHeader?: boolean;

  // Do we define the first column as the category/header column
  disableRowHeader?: boolean;

  // Any data that need to embadded in the generated data.
  seriesOption?: { [name: string]: unknown };
}

/**
 * This interface represent a column on the side panel table
 */
export interface ChartTableColumnSeries<DataType = string> {
  /**
   * The name of the column, usually this is the header of the column
   */
  name?: string;

  /**
   * In combo chart, each column is defined to be one type of chart.
   * Ex: The column B is for the Column chart and the Column C is for the line chart
   *
   * Therefore, this is the controlled header that can be used to define the header of the column.
   * It will be on the header of the handson table,
   */
  controlledHeader?: ControlledHeaderType;

  /**
   * The data of the handson table
   */
  data: DataType[];
}

export type ChartDataSeries = ChartTableColumnSeries<number | string>[];

/**
 * A sub-setting definning the data format in the user's data table
 */
export interface NumberFormatSettingsType {
  /**
   * A switcher for chart to determine showing the percentage in total or displaying the user's input value
   * Default: 'value'.
   */
  format?: 'percentage' | 'value';

  /**
   * Custom serparator in between the numbers
   * Default: 'None'
   */
  separator?: string;

  /**
   * Custom Separator in between the numbers, which belongs to the second y-axis
   * Default: 'None'
   */
  y2Separator?: string;

  /**
   * Defining the precision of the numbers
   * Default: 'Auto'
   */
  precision?: string;

  /**
   * Defining the precision of the numbers, which belongs to the second y-axis
   * Default: 'Auto'
   */
  y2Precision?: string;

  /**
   * @deprecated
   * This property is used on on the pie chart before version 7 and half pie chart before version 2
   * However, the property name does not match this interface we defined and conflict with the usage on other charts.
   * The correct should be named 'precision'.
   * We add this property here as the reference and as a note so that we should avoid using this in the future.
   */
  decimalPrecision?: string;
}

/**
 * Interface that contains common options for
 * all chart types.
 *
 * TODO add other common options - title, labels, bg color, etc.
 */
export interface ChartWidgetData<SeriesDataType = ChartDataSeries> extends AccessibleElement, Widget {
  legend: ChartLegendData;
  dataLabels: ChartLabelDataType;

  /**
   * Charts like the combo chart would have two y-axis, so we provide the second y-axis data label here
   */
  y2DataLabels?: ChartLabelDataType;

  seriesData: SeriesDataType;
  seriesMeta: {
    colors: Color[];
    /**
     * Color Steps is used to define the number of the colors we will use in the chart
     *
     * In most charts, each data point will have a color,
     * so the number of colors is dynamic based on the data.
     * In a few situations, such as word cloud we want to specify the number
     * of colors rotatively no matter how many data points we have
     *
     * default: undefined
     */
    colorSteps?: number;

    /**
     * This is min and max range of the data, which is usually refer to the y axis value
     * For example, if the data is [[2018, 200], [2019, 300]], and the min and max value is [100, 500],
     * Then the y axis is expected to be from 100 to 500
     */
    minValue?: string;
    maxValue?: string;

    /**
     * This is min and max range of the second y-axis, which is usually refer to the y axis value.
     * Ex: the y2-axis of the combo chart
     */
    minY2Value?: string;
    maxY2Value?: string;

    /**
     * This is min and max range of the x-axis, which is can be used to opt out the data that are out of the range
     * For example, if the data is [[2017, 250],[2018, 200], [2019, 300]], and the min and max value is [2018, 2020],
     * Then the chart would use only the data of 2018 and 2019, which is [[2018, 200], [2019, 300]]]
     */
    minXValue?: string;
    maxXValue?: string;

    /**
     * The number of data in the series we should use to generate the chart
     * By default, we use all the data in the series.
     * However, if the user want to limit the number of data in the chart, they can use this option
     *
     * default: undefined
     */
    dataCount?: number;
  };
  xAxis: xAxis;
  yAxis: yAxis;

  /**
   * This is the second y axis, which is used in the combo chart
   */
  y2Axis?: yAxis;
  grid: Grid;
  generalOptions: {
    borderWidth: number;
    borderColor: string;
    innerSize: number;

    /**
     * A optional pasage that can be used to generate a table with data
     * For example, we can extract thw word and put the word count in the table
     *
     * default: undefined
     */
    passage?: string;
  };
  patterns: {
    enabled: boolean;
    list: PatternId[];
  };
  numberFormat?: NumberFormatSettingsType;
  widgetId?: WidgetId;
}

export enum ChartType {
  Table = 'table',
  PieChart = 'piechart',
}

/**
 * Combo chart would have a dropdown on the column header to control the purpose of the data
 * This type list the possible values of the dropdown
 */
export enum ControlledHeader {
  ComboColumn = 'y1',
  ComboLine = 'y2',
}
export type ControlledHeaderType = ControlledHeader;
export const isControlledHeader = (value: unknown): value is ControlledHeaderType => {
  return value === ControlledHeader.ComboColumn || value === ControlledHeader.ComboLine;
};
